/**
 * Encrypted local custody for letter recordings (letter-first flow).
 *
 * Free-tier recordings live on this device, encrypted at rest with the
 * SAME key hierarchy documents use: a fresh per-recording key, wrapped
 * (v2-encrypted) under the user's master recovery key. Successor and
 * keyholder access inherits automatically — anyone who can unwrap the
 * master key can unwrap the recording key.
 *
 * Cloud upload for the letter tier rides Phase 6 with delivery; nothing
 * here talks to the network.
 *
 * Size note: this single-shot encrypt path is safe for audio (a 10-minute
 * AAC file is ~10MB). Video does NOT go through here — it needs the
 * chunked container (see docs/MEDIA_ENCRYPTION_CHUNKED.md) before it can
 * ship.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import RNFS from 'react-native-fs';
import * as Keychain from 'react-native-keychain';

import { createLogger } from '../../utils/logger';
import { DocumentEncryptionService } from '../documentStorageService';
import {
  encrypt as v2Encrypt,
  decrypt as v2Decrypt,
  generateKey as v2GenerateKey,
  serializeEnvelope,
  parseEnvelope,
} from '../encryption/DocumentEncryptionV2';
import { isWrappedMasterKeyEnrolled } from '../encryption/MasterKeyService';
import { vaultService } from '../vaultService';

const logger = createLogger('letterRecordingStorage');

const REGISTRY_KEY = '@letter_recordings_v1';
const RECORDINGS_DIR = `${RNFS.DocumentDirectoryPath}/letter_recordings`;

export interface LetterRecordingMeta {
  id: string;
  letterId: string;
  kind: 'audio';
  durationSecs: number;
  createdAt: string;
  /**
   * The encrypted file's name inside the recordings folder. Older entries
   * hold an absolute path; never use either directly — iOS renames the
   * app's container on every reinstall and Xcode build, so an absolute
   * path goes stale while the file itself survives. resolveEncryptedPath
   * joins the name with the folder as it is right now.
   */
  encryptedPath: string;
  /** JSON-serialized v2 envelope of the per-recording key, wrapped under the master key. */
  encryptedKey: string;
  /** Set once the encrypted envelope reached S3 (letter tier+). */
  uploadedAt?: string;
  remoteId?: string;
}

const encryptionService = new DocumentEncryptionService();

/** Where the recording's encrypted file is today, whatever the registry says. */
export function resolveEncryptedPath(meta: Pick<LetterRecordingMeta, 'encryptedPath'>): string {
  const name = meta.encryptedPath.split('/').pop() || meta.encryptedPath;
  return `${RECORDINGS_DIR}/${name}`;
}

// A v2 combined key is 64 bytes = 128 hex chars (32 AES + 32 HMAC). A
// 64-hex key is v1-era material.
const V2_MASTER_KEY_HEX_LEN = 128;
// Same identifier DocumentEncryptionService uses for its keychain backup.
const LEGACY_KEYCHAIN_ID = 'document_master_key';

async function getMasterKey(): Promise<string> {
  const existing = await encryptionService.getMasterRecoveryKey();
  if (existing && existing.length === V2_MASTER_KEY_HEX_LEN) return existing;
  if (!existing) {
    // Fresh v2 users haven't set a secret code yet — same auto-generate
    // path documents use. Fails closed if enrollment state can't be read.
    return encryptionService.autoGenerateMasterKey();
  }
  return rotateStaleV1Key();
}

/**
 * A v1-format (64-hex) master key on an account that has never encrypted
 * anything is poison, not history. The known path here: an account is
 * deleted server-side (beta reset) while the device keychain still holds
 * its old key; the legacy fallback then resurrects the stale key into
 * the NEW account's vault. v2 encryption can't use it, and there is no
 * data it protects.
 *
 * Rotation is allowed ONLY when all three hold — each guard fails closed:
 *   1. no wrapped-master-key enrollment (an enrolled account's material
 *      is never touched — that is UnlockVault territory)
 *   2. no local encrypted documents (a pre-v2 account with real data
 *      belongs to the v1→v2 migration flow, not to us)
 *   3. no recordings in our own registry
 * Then a fresh v2 key overwrites both the vault copy and the keychain
 * backup. Same primitives, same storage locations — key lifecycle only.
 */
async function rotateStaleV1Key(): Promise<string> {
  const enrolled = await isWrappedMasterKeyEnrolled();
  if (enrolled) {
    throw new Error(
      'account has a wrapped master key but a legacy-format local key — unlock the vault first',
    );
  }
  const storedDocs = await AsyncStorage.getItem('local_documents');
  const docCount = storedDocs ? Object.keys(JSON.parse(storedDocs) || {}).length : 0;
  if (docCount > 0) {
    throw new Error(
      'a legacy-format master key with existing documents needs the migration flow',
    );
  }
  const recordings = await readRegistry();
  if (recordings.length > 0) {
    throw new Error(
      'a legacy-format master key with existing recordings cannot be rotated automatically',
    );
  }

  logger.warn('Rotating stale v1-format master key on an empty account');
  const freshKey = await v2GenerateKey();
  await vaultService.storeMasterRecoveryKey(freshKey);
  try {
    await Keychain.setInternetCredentials(LEGACY_KEYCHAIN_ID, 'master_key', freshKey);
  } catch (keychainError) {
    logger.debug('Keychain overwrite failed (non-critical):', keychainError);
  }
  return freshKey;
}

async function readRegistry(): Promise<LetterRecordingMeta[]> {
  try {
    const raw = await AsyncStorage.getItem(REGISTRY_KEY);
    return raw ? (JSON.parse(raw) as LetterRecordingMeta[]) : [];
  } catch (error) {
    logger.error('Registry read failed:', error);
    return [];
  }
}

async function writeRegistry(list: LetterRecordingMeta[]): Promise<void> {
  await AsyncStorage.setItem(REGISTRY_KEY, JSON.stringify(list));
}

class LetterRecordingStorage {
  /**
   * Encrypt a finished recording and take custody of it. The plaintext
   * source file (the recorder's temp output) is deleted afterwards so no
   * unencrypted copy lingers.
   */
  async store(
    letterId: string,
    sourceUri: string,
    durationSecs: number,
  ): Promise<LetterRecordingMeta> {
    // Stage tracking so a failure names the step, not just "could not
    // save" — the difference between a vault issue and a filesystem one.
    let stage = 'getting encryption key';
    try {
      const masterKey = await getMasterKey();

      stage = 'generating recording key';
      const recordingKey = await v2GenerateKey();
      const keyEnvelope = await v2Encrypt(recordingKey, masterKey);

      stage = 'reading recording file';
      const sourcePath = sourceUri.replace('file://', '');
      const contentBase64 = await RNFS.readFile(sourcePath, 'base64');

      stage = 'encrypting recording';
      const fileEnvelope = await v2Encrypt(contentBase64, recordingKey);

      stage = 'writing encrypted file';
      const dirExists = await RNFS.exists(RECORDINGS_DIR);
      if (!dirExists) await RNFS.mkdir(RECORDINGS_DIR);

      const id = `rec_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const fileName = `${id}.enc`;
      await RNFS.writeFile(`${RECORDINGS_DIR}/${fileName}`, serializeEnvelope(fileEnvelope), 'utf8');
      return await this.commitMeta(letterId, sourcePath, durationSecs, id, fileName, keyEnvelope);
    } catch (error: any) {
      const detail = error?.message || String(error);
      logger.error(`store failed while ${stage}:`, error);
      throw new Error(`${stage}: ${detail}`);
    }
  }

  private async commitMeta(
    letterId: string,
    sourcePath: string,
    durationSecs: number,
    id: string,
    encryptedPath: string,
    keyEnvelope: Parameters<typeof serializeEnvelope>[0],
  ): Promise<LetterRecordingMeta> {

    const meta: LetterRecordingMeta = {
      id,
      letterId,
      kind: 'audio',
      durationSecs,
      createdAt: new Date().toISOString(),
      encryptedPath,
      encryptedKey: serializeEnvelope(keyEnvelope),
    };

    const registry = await readRegistry();
    registry.push(meta);
    await writeRegistry(registry);

    try {
      await RNFS.unlink(sourcePath);
    } catch (cleanupError) {
      logger.debug('Plaintext source cleanup failed (non-critical):', cleanupError);
    }

    logger.debug('Recording stored encrypted:', id);
    return meta;
  }

  async list(letterId?: string): Promise<LetterRecordingMeta[]> {
    const registry = await readRegistry();
    return letterId ? registry.filter((r) => r.letterId === letterId) : registry;
  }

  /**
   * Decrypt a recording to a temp file for playback. Callers must invoke
   * cleanupTemp when playback is done.
   */
  async decryptToTempFile(meta: LetterRecordingMeta): Promise<string> {
    const masterKey = await getMasterKey();
    const recordingKey = await v2Decrypt(parseEnvelope(meta.encryptedKey), masterKey);

    const raw = await RNFS.readFile(resolveEncryptedPath(meta), 'utf8');
    const contentBase64 = await v2Decrypt(parseEnvelope(raw), recordingKey);

    const tempPath = `${RNFS.CachesDirectoryPath}/play_${meta.id}.m4a`;
    await RNFS.writeFile(tempPath, contentBase64, 'base64');
    return tempPath;
  }

  async cleanupTemp(path: string): Promise<void> {
    try {
      if (await RNFS.exists(path)) await RNFS.unlink(path);
    } catch (error) {
      logger.debug('Temp cleanup failed (non-critical):', error);
    }
  }

  /** Record that a recording's ciphertext reached the cloud. */
  async markUploaded(id: string, remoteId: string): Promise<void> {
    const registry = await readRegistry();
    const meta = registry.find((r) => r.id === id);
    if (!meta) return;
    meta.uploadedAt = new Date().toISOString();
    meta.remoteId = remoteId;
    await writeRegistry(registry);
  }

  async remove(id: string): Promise<void> {
    const registry = await readRegistry();
    const meta = registry.find((r) => r.id === id);
    if (meta) {
      try {
        const path = resolveEncryptedPath(meta);
        if (await RNFS.exists(path)) await RNFS.unlink(path);
      } catch (error) {
        logger.error('Encrypted file delete failed:', error);
      }
    }
    await writeRegistry(registry.filter((r) => r.id !== id));
  }
}

export const letterRecordingStorage = new LetterRecordingStorage();
